import {
  BoundSelectField,
  Button,
  column,
  Css,
  FormLines,
  GridColumn,
  GridDataRow,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SimpleHeaderAndData,
  useComputed,
  useModal,
} from "@homebound/beam";
import { ObjectConfig, required, useFormState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { useMemo, useState } from "react";
import { QueryTable } from "src/components";
import { Maybe } from "src/generated/graphqlTypes";
import { ProductOfferingDetailFragment } from "../ProductOfferingPage.generated";
import {
  TestGroupDependenciesLineItemsQuery,
  TestGroupDependenciesModal_TakeoffLineItemFragment,
  useTestGroupDependenciesLineItemsQuery,
} from "./TestGroupDependenciesModal.generated";

type TestGroupDependenciesModalProps = { productOffering: ProductOfferingDetailFragment };

export function TestGroupDependenciesModal({ productOffering }: TestGroupDependenciesModalProps) {
  const { closeModal } = useModal();
  const [showForm, setShowForm] = useState(true);
  const formState = useFormState({ config: formConfig });
  // This useComputed was necessary for the query to be reactive on form changes.
  const [skip, variables] = useComputed(
    () => [
      !formState.addonOptionId.value || !formState.personalizationOptionGroupId.value,
      {
        addonOptionId: formState.addonOptionId.value!,
        personalizationOptionGroupId: formState.personalizationOptionGroupId.value!,
      },
    ],
    [formState.addonOptionId.value, formState.personalizationOptionGroupId.value],
  );
  const query = useTestGroupDependenciesLineItemsQuery({ skip, variables });

  const addonOptions = useMemo(
    () => productOffering.options.filter((o) => o.type.isAddOnV2 || o.type.isStructuralV2),
    [productOffering.options],
  );
  const personalizationOptionGroups = useMemo(
    () =>
      productOffering.options
        .filter((o) => o.type.isProductOptionV2 || o.type.isPackageOptionV2)
        .map((o) => o.optionGroup)
        .uniqueBy((g) => g.id),
    [productOffering.options],
  );

  const addOnName = useComputed(
    () => addonOptions.find((o) => o.id === formState.addonOptionId.value)?.displayName,
    [addonOptions, formState.addonOptionId.value],
  );

  const addonPrereqChildren = useMemo(() => {
    return query.data?.readyPlanOption?.optionGroup?.groupPrereqChildren ?? [];
  }, [query.data]);

  const personalizationOptionGroupName = useComputed(() => {
    return personalizationOptionGroups.find((o) => o.id === formState.personalizationOptionGroupId.value)?.name;
  }, [personalizationOptionGroups, formState.personalizationOptionGroupId.value]);

  return (
    <>
      <ModalHeader>Test Group Dependencies</ModalHeader>
      <ModalBody>
        {!showForm && (
          <>
            <div css={Css.mb2.gray700.$}>
              More than 1 dependent groups will prevent the Add Option/Choose Option groups from collapsing into a
              single card in checkout. The selected add-on option has the following Groups Dependent on it:
            </div>
            {addonPrereqChildren.isEmpty ? (
              "No Dependent Groups"
            ) : (
              <ul data-testid="dependentGroups">
                {addonPrereqChildren.map((c) => (
                  <li key={c.id}>
                    {c.name}{" "}
                    {c.id === formState.personalizationOptionGroupId.value ? (
                      <span css={Css.green500.$}>(Selected)</span>
                    ) : (
                      ""
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        <div css={Css.mb2.gray700.$}>
          {showForm
            ? "Select a parent Structural Add-on Option and a child Personalization option to test that the child is referencing the correct parent option."
            : `The following slots in '${personalizationOptionGroupName}' option group aren't tagged with '${addOnName}'.`}
        </div>
        {showForm ? (
          <FormLines>
            <BoundSelectField
              options={addonOptions}
              field={formState.addonOptionId}
              compact
              getOptionLabel={(o) => o.displayName}
            />
            <BoundSelectField
              options={personalizationOptionGroups}
              field={formState.personalizationOptionGroupId}
              label="Personalization Option"
              compact
            />
          </FormLines>
        ) : (
          <QueryTable {...{ query, createRows, columns }} />
        )}
      </ModalBody>

      {showForm ? (
        <ModalFooter>
          <Button data-testid="cancelButton" variant="quaternary" label="Cancel" onClick={closeModal} />
          <Observer>
            {() => <Button label={"Submit"} onClick={() => setShowForm(false)} disabled={!formState.valid} />}
          </Observer>
        </ModalFooter>
      ) : null}
    </>
  );
}

type Row = SimpleHeaderAndData<TestGroupDependenciesModal_TakeoffLineItemFragment>;

const columns: GridColumn<Row>[] = [
  column<Row>({ header: "Cost Code", data: ({ item }) => item?.costCode?.number }),
  column<Row>({ header: "Item Code", data: ({ item }) => item?.fullCode }),
  column<Row>({ header: "Name", data: ({ name }) => name }),
  column<Row>({ header: "Location", data: ({ location }) => (location?.room ?? location?.parent)?.name ?? "-" }),
];

function createRows(data: TestGroupDependenciesLineItemsQuery | undefined): GridDataRow<Row>[] {
  return data?.testGroupDependenciesLineItems?.map((l) => ({ kind: "data" as const, id: l.id, data: l })) ?? [];
}

const formConfig: ObjectConfig<{ addonOptionId: Maybe<string>; personalizationOptionGroupId: Maybe<string> }> = {
  addonOptionId: { type: "value", rules: [required] },
  personalizationOptionGroupId: { type: "value", rules: [required] },
};
